import React from 'react';
import KpiRow from '../shared/KpiRow';
import KpiTile from '../shared/KpiTile';
import ChartPanel from '../shared/ChartPanel';
import DateRangePicker from '../shared/DateRangePicker';
import AnalysisTable from '../shared/AnalysisTable';
import { useAnalysisRange } from '../../../hooks/analysis/useAnalysisRange';
import { useAnomalyAnalysis } from '../../../hooks/analysis/useAnomalyAnalysis';
import { AnomalySeverity } from '../../../utils/analytics/types';

// Status severity is always icon + label, never color alone.
const SEVERITY_BADGES: Record<AnomalySeverity, string> = {
  critical: '🟥 Critical',
  serious: '🟧 Serious',
  warning: '🟨 Warning',
  info: 'ℹ️ Info',
};

const TYPE_LABELS: Record<string, string> = {
  far_future_booking: 'Far-future booking',
  past_year_booking: 'Past-year booking',
  off_season_booking: 'Off-season booking',
  duplicate_booking: 'Duplicate booking',
  zero_cost: 'Zero/negative cost',
  long_stay: 'Unusually long stay',
  cancel_rebook: 'Cancel–rebook',
  orphan_version: 'Version chain issue',
  stale_unpaid: 'Stale unpaid',
  gateway_environment: 'Gateway environment',
};

const AnomalyAnalysis: React.FC = () => {
  const { range, setRange } = useAnalysisRange();
  const data = useAnomalyAnalysis(range);

  return (
    <div>
      <DateRangePicker value={range} onChange={setRange} />

      <KpiRow>
        <KpiTile label="Critical" value={String(data.counts.critical)} />
        <KpiTile label="Serious" value={String(data.counts.serious)} />
        <KpiTile label="Warning" value={String(data.counts.warning)} />
        <KpiTile label="Info" value={String(data.counts.info)} />
      </KpiRow>

      <ChartPanel
        title="Findings"
        subtitle="Rule-based checks over the loaded reservations and payments, most severe first"
        loading={data.loading}
        error={data.error}
        empty={data.findings.length === 0}
        emptyMessage="No anomalies detected in this range. 🎉"
        footnote="Detectors: out-of-season/far-future bookings, duplicates, zero-cost, long stays, cancel–rebook patterns, version-chain issues, stale unpaid confirmations, and gateway environment checks."
      >
        <AnalysisTable
          columns={[
            { key: 'severity', label: 'Severity', format: v => SEVERITY_BADGES[v as AnomalySeverity] },
            { key: 'type', label: 'Type', format: v => TYPE_LABELS[String(v)] ?? String(v) },
            { key: 'confirmationCode', label: 'Confirmation' },
            { key: 'message', label: 'Detail' },
          ]}
          rows={data.findings}
          maxRows={50}
        />
      </ChartPanel>
    </div>
  );
};

export default AnomalyAnalysis;
